/* System defined core library */
import { Component, HostListener, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../../bootstrap-params';
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { AppointmentService } from '../../shared/services/appointment/appointment.service';
import { MessagingApiService } from '../../shared/services/messaging-api.service';
import { MetricApiService } from '../../shared/services/metric-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { EmailResponse } from '../../../shared/models/common/shared.model';
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { BatteryConfig } from '../../../shared/models/scheduling/batteryConfig.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { PersonDetail } from '../../../shared/models/scheduling/personalDetail.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { CreatePaymentSessionResponse } from '../../../shared/models/scheduling/paymentSession.model';

/* User defined enums/constants */
import { SchedulingErrors, TermUrlConstants } from '../../../shared/constants/scheduling.constant';
import { ComponentType, LogLevel } from '../../../shared/enums/scheduling.enum';

/* Images */
const validateImg = require('/Assets/images/confirmation/validate.png');
const moneyImg = require('/Assets/images/confirmation/money.png');
const createCalendarImg = require('/Assets/images/confirmation/createcalendar.png');

@Component({
  selector: 'app-cancellation-confirmation',
  templateUrl: './cancellation-confirmation.component.html'
})
export class CancellationConfirmationComponent implements OnInit {

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: any) {
    if (this.isBookingInProgress) {
      let confNum = '';
      if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
        confNum = this.selectedAppointments[0].exam.sessions[0].confirmationNumber;
      } else {
        confNum = this.selectedAppointments[0].ConfirmationNumber;
      }

      this.metricApiService.log(LogLevel.Debug, `Candidate unloaded the Confirmation page while booking was in progress.\nConfirmationNumber: ${confNum}`);
    }
  }

  /* User defined private/public variables */
  public validate = validateImg;
  public money = moneyImg;
  public createCalendar = createCalendarImg;
  public showPostingInProgress: boolean;
  public postedConfirmationNumber: string;
  public candidate: Candidate;
  public selectedAppointments: AppointmentDetail[] = []
  public cancelledConfirmationList: string[] = [];
  public personDetail: PersonDetail;
  public totalAmount: number = 0.0;
  public refundCurrency: string;
  public refundAmount: number;
  public currencyCode: string;
  public emailResponse: EmailResponse;
  public terminationUrl: string = "";
  public terminationText: string = 'reconfirmationComponent.termUrl';
  public pathLock: boolean = false;
  public batteryConfig: BatteryConfig;
  public paymentOrchestratorSessionDetail: CreatePaymentSessionResponse;
  public examDetailsForSettlement: any;
  public appointmentIds: string[];
  public examAccommodationsForNativeDelete: any[];
  public voucherPayment: Payment[];
  public errorMessage: any;
  public skipConfirmPage: boolean = false;
  public isBookingInProgress: boolean = false;

  /* Service subscriptions */
  batteryConfig$: Subscription;
  booking: Booking;
  booking$: Subscription;
  candidateData$: Subscription;
  selectedAppointments$: Subscription;
  cancelledConfirmationList$: Subscription;
  translateLanguage$: Subscription;
  paymentOrchestratorSessionDetail$: Subscription;
  examDetailsForSettlement$: Subscription;
  appointmentIds$: Subscription;
  examAccommodationsForNativeDelete$: Subscription;
  voucherPayment$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private apptService: AppointmentService,
    private messagingApiService: MessagingApiService,
    private routingService: RoutingService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private schedulingApiService: SchedulingApiService,
    private metricApiService: MetricApiService,
    private bootstrapParams: BootstrapParams
  ) {
    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);

    this.candidateData$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
        if (this.candidate) {
          this.personDetail = new PersonDetail(this.candidate.FirstName, this.candidate.LastName,
            this.candidate.AddressLine1, this.candidate.AddressLine2, this.candidate.AddressLine3,
            this.candidate.PrimaryPhoneNumber, this.candidate.EmailAddress);
        }
      });

    this.booking$ = selectedExamDetailService.booking.subscribe(
      booking => {
        this.booking = booking;
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.cancelledConfirmationList$ = selectedExamDetailService.postedConfirmationList.subscribe(
      postedConfirmationList => {
        this.cancelledConfirmationList = postedConfirmationList;
      });

    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });


    this.batteryConfig$ = selectedExamDetailService.batteryConfig.subscribe(
      batteryConfig => {
        this.batteryConfig = batteryConfig;
      });

    this.paymentOrchestratorSessionDetail$ = selectedExamDetailService.paymentOrchestratorSessionDetail.subscribe(
      paymentOrchestratorSessionDetail => {
        this.paymentOrchestratorSessionDetail = paymentOrchestratorSessionDetail;
      });

    this.examDetailsForSettlement$ = selectedExamDetailService.examDetailsForSettlement.subscribe(
      examDetailsForSettlement => {
        this.examDetailsForSettlement = examDetailsForSettlement;
      });

    this.appointmentIds$ = selectedExamDetailService.appointmentIds.subscribe(
      appointmentIds => {
        this.appointmentIds = appointmentIds;
      });

    this.examAccommodationsForNativeDelete$ = selectedExamDetailService.examAccommodationsForNativeDelete.subscribe(
      examAccommodationsForNativeDelete => {
        this.examAccommodationsForNativeDelete = examAccommodationsForNativeDelete;
      });

    this.voucherPayment$ = selectedExamDetailService.voucherPayment.subscribe(
      payment => {
        this.voucherPayment = payment;
      });

    this.totalAmount = this.selectedExamDetailService.cancellationFee['totalAmount'];
    this.currencyCode = this.selectedExamDetailService.cancellationFee['currency'];
    this.refundCurrency = this.selectedExamDetailService.cancelRefundCurrency;
    this.refundAmount = this.selectedExamDetailService.cancelRefund;

    this.pathLock = this.selectedExamDetailService.setCancelPathLock;
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    let clientCode = this.selectedExamDetailService.clientCode ? this.selectedExamDetailService.clientCode.toLowerCase() : '';
    if (clientCode === 'cfa' || clientCode === 'cfad' || clientCode === 'cfas') {
      this.terminationText = 'CFA_Cancel_Term'
    }

    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.CancellationConfirmationComponent)) return;

    this.postedConfirmationNumber = this.selectedExamDetailService.postedConfirmationNumber;
    this.skipConfirmPage = this.selectedExamDetailService.skipConfirmPage;
    this.terminationUrl = this.selectedExamDetailService.terminationUrl;

    if (this.selectedExamDetailService.isPaymentRequired) {
      this.isBookingInProgress = true;
      this.schedulingApiService.completeBookingCancel(
        this.appointmentIds,
        this.examAccommodationsForNativeDelete,
        this.paymentOrchestratorSessionDetail,
        this.examDetailsForSettlement,
        this.voucherPayment,
        this.selectedAppointments[0].ConfirmationNumber
      ).subscribe(
        (resp) => {
          this.showPostingInProgress = false;
          window.localStorage.removeItem(this.bootstrapParams.sessionGuid);

          resp.confirmationNumber = "";
          this.cancelledConfirmationList = [];
          if (!!resp && resp.status) {
            resp.response.tests.forEach((test: any) => {
              if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length) {
                resp.confirmationNumber = resp.confirmationNumber
                  .concat(test.sessions[0].confirmationNumber + " (" + this.selectedAppointments[0].exam.name + ") <br />");
              } else {
                let selectedAppt = this.selectedAppointments.filter((selectedAppointment: any) => { return selectedAppointment.ConfirmationNumber == test.sessions[0].confirmationNumber });

                resp.confirmationNumber = resp.confirmationNumber
                  .concat(test.sessions[0].confirmationNumber + " (" + selectedAppt[0].exam.name + ") <br />");
              }
              this.cancelledConfirmationList.push(test.sessions[0].confirmationNumber);
            });
            this.selectedExamDetailService.setPostedConfirmationList(this.cancelledConfirmationList);

            // Remove </br> tag end of the string
            resp.confirmationNumber = resp.confirmationNumber.substring(0, resp.confirmationNumber.length - 6);
            //Saved state in service if needed for other component
            this.selectedExamDetailService.isPaymentConfirmed = true;
            //Saved state in service for confirmation component
            this.selectedExamDetailService.postedConfirmationNumber = resp.confirmationNumber;
            this.postedConfirmationNumber = this.selectedExamDetailService.postedConfirmationNumber;
            this.selectedExamDetailService.postedErrorMessage = "";

            this.selectedAppointments.forEach((appointment) => {
              appointment.status = "cancelled"
            });

            this.isBookingInProgress = false;

            if (this.skipConfirmPage) {
              this.goToTerminationUrl();
            }

          } else if (resp.errorId) {
            this.isBookingInProgress = false;
            this.errorMessage = {
              id: resp.errorId,
              message: 'errors.error',
              description: resp.message
            };
            this.postedConfirmationNumber = 'Error';
          }
        }, (err) => {
          this.isBookingInProgress = false;
          this.showPostingInProgress = false;
          window.localStorage.removeItem(this.bootstrapParams.sessionGuid);

          this.errorMessage = {
            id: SchedulingErrors.Default,
            message: 'errors.error',
            description: 'errors.thereWasAnIssue'
          };
          this.postedConfirmationNumber = 'Error';
        });
    } else {
      this.showPostingInProgress = false;

      this.selectedAppointments.forEach((appointment) => {
        appointment.status = "cancelled"
      });
    }
  }


  /* use to send confirmation or Receipt mail */
  email(emailto: string, emailtype: string): void {
    $(".email-list-icon").addClass('no-outline');
    let emailResps: EmailResponse[] = [];
    if (this.cancelledConfirmationList.length > 0) {
      this.cancelledConfirmationList.forEach((confirmation) => {
        this.messagingApiService.email(emailto, emailtype, confirmation).subscribe(mailResponse => {
          let emailResp: EmailResponse = new EmailResponse();
          emailResp.successful = mailResponse.successful;
          emailResp.emailType = (emailtype == "eConfirmation") ? "confirmation" : "receipt";
          emailResps.push(emailResp);
          if (emailResps.length == this.cancelledConfirmationList.length) {
            this.emailResponse = emailResp;
            this.messagingApiService.setEmailState(emailResp);
          }
        }, (err) => {
          let emailResponse: EmailResponse = new EmailResponse();
        });
      })
    }
  }

  print() {
    $(".print-list-icon").addClass('no-outline');
  }

  /* Move to previous screen */
  goBack(): void {
    this.routingService.backwardNavigation(ComponentType.CancellationConfirmationComponent);
  }

  /* Go back to home */
  goHome(event: any): void {
    this.routingService.homeNavigation();
  }

  /* Go to client page after appointment is scheduled. */
  goToTerminationUrl(): void {
    this.routingService.navigateToTerm(TermUrlConstants.Canceled);
  }

  /* Navigate away from proscheduler(to term/prometric site) */
  goToPrometric(): void {
    this.routingService.navigateAwayFromProscheduler();
  }

  /* Make Search performed false if component is unloaded */
  ngOnDestroy() {
    this.selectedExamDetailService.isSearchPerformed = false;
    this.selectedExamDetailService.cancellationFee = {
      totalAmount: 0,
      currency: null
    };
    this.selectedExamDetailService.setPostedConfirmationList(null);
  }

  //TODO: look into moving all copies of this method throughout the app into ngOnInit -MR
  getApptDetail(appointmentDetail: AppointmentDetail, sessionIndex: number) {
    if (appointmentDetail.exam.sessions && appointmentDetail.exam.sessions.length < 1) return appointmentDetail;
    const session = appointmentDetail.exam.sessions[sessionIndex];
    let appointmentSlot =
      new AppointmentSlot(null, null, new Date(this.utilityService.getFormatToFullMMDDYYYY(session.slot.startDateTime, "/")), session.duration, session.site, session.slot.testTime, session.slot.testEndTime);
    appointmentDetail.appointmentSlot = appointmentSlot;
    return appointmentDetail;
  }


  isDisable(): boolean {
    return this.showPostingInProgress;
  }

  canShowReceiptBtn(): boolean {
    return this.apptService.canShowReceiptBtn(this.selectedAppointments);
  }

  isBookingComplete(): boolean {
    return !!this.postedConfirmationNumber && !this.errorMessage && !this.selectedExamDetailService.skipConfirmPage;
  }

  isBookingError(): boolean {
    return !!this.errorMessage && !!this.errorMessage.id;
  }

  showWaitView(): boolean {
    return !this.isBookingError() && (!this.postedConfirmationNumber || this.skipConfirmPage)
  }
}